import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useSystemRisk } from '@/api/hooks/use-system';

const RECOVERY_DISPLAY_MS = 8_000;

/**
 * Displays a non-dismissible red alert banner when system risk limits are breached.
 * Shows a green recovery banner for 8 seconds when breach status resolves.
 * Reads from /system/risk via polling (30s interval).
 */
export function AlertBanner() {
  const { t } = useTranslation('risk');
  const { data } = useSystemRisk();
  const [prevBreached, setPrevBreached] = useState<boolean | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);

  const breached = data ? data.system_exposure_breached || data.system_daily_loss_breached : false;

  // React-recommended pattern: setState during render for derived state transitions
  // See https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (data && prevBreached !== breached) {
    setPrevBreached(breached);
    if (prevBreached === true && !breached) {
      setShowRecovery(true);
    }
    if (breached) {
      setShowRecovery(false);
    }
  }

  // Auto-dismiss recovery banner after timeout
  useEffect(() => {
    if (!showRecovery) return;
    const timer = setTimeout(() => setShowRecovery(false), RECOVERY_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [showRecovery]);

  if (!data) return null;

  if (showRecovery && !breached) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full bg-green-600 text-white px-4 py-2.5 flex items-center gap-3"
      >
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-sm font-medium">{t('alerts.recovered')}</span>
      </div>
    );
  }

  if (!breached) return null;

  const messages: string[] = [];
  if (data.system_exposure_breached) messages.push(t('alerts.exposureBreached'));
  if (data.system_daily_loss_breached) messages.push(t('alerts.dailyLossBreached'));

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="w-full bg-destructive text-destructive-foreground px-4 py-2.5 flex items-center gap-3"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <div className="flex-1 text-sm font-medium">
        {messages.join(' · ')}
        {data.halted_bots > 0 && (
          <span className="ml-2 opacity-80">
            {t('alerts.botsHalted', { count: data.halted_bots })}
          </span>
        )}
      </div>
    </div>
  );
}
